import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { ApiContext } from "../../api-context";

import type { StatisticsSuccessResponse } from "@sperrdatei/dto/types";

type DownloadPageProps = object;

const formatNumber = (value?: number): string => {
  if (!value) {
    return "";
  }

  return Intl.NumberFormat("de-DE").format(value);
};

const formatPercent = (value?: number): string => {
  if (!value) {
    return "";
  }

  return (value * 100).toFixed(1) + "%";
};

export const DownloadPage: React.FC<DownloadPageProps> = () => {
  const { apiFetch } = useContext(ApiContext);
  const navigate = useNavigate();

  const [stats, setStats] = useState<StatisticsSuccessResponse | undefined>();
  const [isDownloadActive, setIsDownloadActive] = useState<boolean>(false);

  const initBrowserDownload = (blob: Blob, name = "sperrnummern.zip"): void => {
    const href = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement("a"), {
      href,
      style: "display:none",
      download: name,
    });
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(href);
    a.remove();
  };

  const getFile = async (): Promise<void> => {
    const response = await apiFetch("/api/number/download");

    if (response.ok) {
      const reader = response.body?.getReader();
      const chunks = [];

      let loaded = 0;
      if (reader) {
        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          chunks.push(value);
          loaded += value.length;

          setIsDownloadActive(true);
        }
      }

      setIsDownloadActive(false);
      console.log(`Download completed: ${loaded} bytes total`);
      const content = new Blob(chunks);

      initBrowserDownload(content);
      return;
    }

    navigate("/error");
  };

  const loadStatistics = useCallback(async (): Promise<void> => {
    const response = await apiFetch("/api/number/stats");
    if (response.ok) {
      const stats = (await response.json()) as StatisticsSuccessResponse;
      setStats(stats);
    }
  }, [apiFetch]);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  return (
    <div className="container ms-0">
      <h3 className="page-caption mb-3">Download</h3>
      <div className="col-xxl-7 col-xl-7 col-lg-10 col-xs-12 mt-0 p-0">
        <p>
          Hier können Sie die aktuelle Liste aller gesperrten Nummern als
          CSV-Datei herunterladen.
        </p>

        <div
          className="input-group mb-3"
          onClick={() => {
            if (!isDownloadActive) {
              getFile();
            }
          }}
        >
          <span
            className="input-group-text border border-1 border-secondary-subtle"
            id="basic-addon1"
          >
            {isDownloadActive ? (
              <>
                <div className="spinner-grow spinner-grow-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </>
            ) : (
              <i className="bi bi-download"></i>
            )}
          </span>
          <button className="btn btn-light border border-1 border-secondary-subtle">
            Download der Sperrdatei
          </button>
        </div>

        <div className="p-3 border rounded border-1 border-secondary-subtle d-none d-sm-block">
          <p>
            <strong>Umfang der Sperrdatei</strong>
          </p>
          <table className="table table-sm text-center">
            <thead>
              <tr>
                <th className="text-start">
                  Art der
                  <br />
                  Rufnummer
                </th>
                <th>
                  Alle
                  <br />
                  Befragungen
                </th>
                <th>
                  Bevölkerungs-
                  <br />
                  befragungen
                </th>
                <th>Gesamt</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="text-start">
                  <strong>Privat</strong>
                </td>
                <td>
                  {formatNumber(stats?.privateNumbers)}
                  <br />
                  <span className="fw-light">
                    {formatPercent(
                      stats ? stats.privateNumbers / stats.overall : 0,
                    )}
                  </span>
                </td>
                <td></td>
                <td>
                  {formatNumber(stats?.privateNumbers)}
                  <br />
                  <span className="fw-light">
                    {formatPercent(
                      stats ? stats.privateNumbers / stats.overall : 0,
                    )}
                  </span>
                </td>
                <td></td>
              </tr>
              <tr>
                <td className="text-start">
                  <strong>Gewerblich</strong>
                </td>
                <td>
                  {formatNumber(stats?.commercialNumbersBlockedForAllSurveys)}
                  <br />
                  <span className="fw-light">
                    {formatPercent(
                      stats
                        ? stats.commercialNumbersBlockedForAllSurveys /
                            stats.overall
                        : 0,
                    )}
                  </span>
                </td>
                <td>
                  {stats
                    ? formatNumber(
                        stats.commercialNumbers -
                          stats.commercialNumbersBlockedForAllSurveys,
                      )
                    : 0}
                  <br />
                  <span className="fw-light">
                    {formatPercent(
                      stats
                        ? (stats.commercialNumbers -
                            stats.commercialNumbersBlockedForAllSurveys) /
                            stats.overall
                        : 0,
                    )}
                  </span>
                </td>
                <td>
                  {formatNumber(stats?.commercialNumbers)}
                  <br />
                  <span className="fw-light">
                    {formatPercent(
                      stats ? stats.commercialNumbers / stats.overall : 0,
                    )}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="border-0 text-start">
                  <strong>Gesamt</strong>
                </td>
                <td className="border-0">
                  {stats
                    ? formatNumber(
                        stats.privateNumbers +
                          stats.commercialNumbersBlockedForAllSurveys,
                      )
                    : 0}

                  <br />
                  <span className="fw-light">
                    {formatPercent(
                      stats
                        ? (stats.privateNumbers +
                            stats.commercialNumbersBlockedForAllSurveys) /
                            stats.overall
                        : 0,
                    )}
                  </span>
                </td>
                <td className="border-0">
                  {stats
                    ? formatNumber(
                        stats.commercialNumbers -
                          stats.commercialNumbersBlockedForAllSurveys,
                      )
                    : 0}

                  <br />
                  <span className="fw-light">
                    {formatPercent(
                      stats
                        ? (stats.commercialNumbers -
                            stats.commercialNumbersBlockedForAllSurveys) /
                            stats.overall
                        : 0,
                    )}
                  </span>
                </td>
                <td className="border-0">
                  {formatNumber(stats?.overall)}
                  <br />
                  <span className="fw-light">100%</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="mt-3">
          {stats && stats.addedLastWeek > 0 && (
            <>
              In den letzten 7 Tagen
              {stats.addedLastWeek > 1
                ? ` insgesamt wurden ${formatNumber(stats.addedLastWeek)} Rufnummern `
                : ` insgesamt wurde ${formatNumber(stats.addedLastWeek)} Rufnummer `}
              neu zur Sperrdatei hinzugefügt.
            </>
          )}
        </p>
      </div>
    </div>
  );
};
